import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Card, Grid, CardContent, Button, Typography, Dialog, DialogContent, Chip, FormControl, FormControlLabel, MenuItem, IconButton, styled } from '@mui/material';
import SearchBox from '../../components/searchbox';
import DropZone from '../files/dropzone';
import axios from 'axios';
import Config from '../../config';
import DownloadButton from '../../components/download_button';
import isEqual from 'lodash.isequal';
import { SimpleRte } from '../../components/EnhancedRte';
import { Form, Field } from 'react-final-form';
import Switch from '../../components/switch';
import { FieldArray } from 'react-final-form-arrays';
import arrayMutators from 'final-form-arrays';
import TextField from '../../components/text_field';
import SelectField from '../../components/select_field';
import { LabelledSwitch as SwitchField } from '../../components/switch';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBack from '@mui/icons-material/ArrowBack';
import ConfirmDialog from '../../components/confirm_dialog';
import ExtendedTextField from '../../components/text_field';
import Helpers from '../../helpers';
// iconButton styling moved to inline sx prop
import { RootGrid as RootGridBase, HeaderTypography as HeaderTypographyBase } from '../catalogmanager/containers/settings/containers/taxonomy';
import { FastSelect, fieldTypes, scheduleOptions, ConfirmSelectField } from './onboarding_settings';
import SectionToolbar from './section_toolbar';
import { pageContentGap } from '../../theme';
import {
    OnboardingFormSettings,
    OnboardingReminderSettings,
    OnboardingStepSettings,
    TestCaseGroupsState,
    OnboardingAgreement,
} from './onboarding_settings.types';

// RootGrid/HeaderTypography come from an untyped .js file and are built with
// MUI's styled(), which (like the forwardRef-wrapped components elsewhere in
// this migration, e.g. ExtendedSelect) infers a prop type too narrow to
// accept container/style/children — cast to ComponentType<any>.
const RootGrid = RootGridBase as React.ComponentType<any>;
const HeaderTypography = HeaderTypographyBase as React.ComponentType<any>;

// Adding the `selected` custom prop via styled()'s generic loses Button's
// polymorphic `component` prop (used below for `component='a'`); relax to
// `any` the same way ListItem's legacy `button` prop is handled elsewhere.
const JumpToTabBase = styled(Button)<{ selected?: number }>(({theme, selected}) => ({
    padding: "12px 20px",
    border: "2px solid",
    borderColor: selected ? theme.palette.tertiary.main : "#DCDCDC",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: 600,
    color: selected ? theme.palette.tertiary.main : "#7B7B7B",
    justifyContent: "flex-start",
    backgroundColor: "#FFFFFF",
    boxSizing: "border-box",
    transition: "border-color 0.2s ease, color 0.2s ease",
    "&:hover": {
        borderColor: theme.palette.tertiary.main,
        color: theme.palette.tertiary.main,
        backgroundColor: "#FFFFFF"
    }
}));
const JumpToTab: any = JumpToTabBase;
const iconButtonSx = (theme: any) => ({
    backgroundColor: "white",
    border: "none",
    borderRadius: "6px",
    "& svg": { color: "rgba(0, 0, 0, 0.54)" }
});

interface OnboardingSettingsV2Props {
    history?: any;
    location?: any;
    setTitle?: (title: string, includeInRecents?: boolean) => void;
    isRetailer?: boolean;
    isBillable?: boolean;
}

interface JumpSection {
    href: string;
    label: string;
    hidden?: boolean;
}

const OnboardingSettingsV2 = ({ history, location, setTitle }: OnboardingSettingsV2Props) => {
    const [tags, setTags] = useState<string[]>([]);
    const [loadedTags, setLoadedTags] = useState<string[]>([]);
    const [filteredTags, setFilteredTags] = useState<string[]>([]);
    const [tagTerm, setTagTerm] = useState('');
    const [stageMessage, setStageMessage] = useState('');
    const [productionMessage, setProductionMessage] = useState('');
    const [form, setForm] = useState<Partial<OnboardingFormSettings>>({});
    const [reminders, setReminders] = useState<Partial<OnboardingReminderSettings>>({});
    const [steps, setSteps] = useState<Partial<OnboardingStepSettings>>({});
    const [testCases, setTestCases] = useState<TestCaseGroupsState>({ Enabled: false, Groups: [], Details: [] });
    const [groupTerm, setGroupTerm] = useState('');
    const [activeJump, setActiveJump] = useState('#tags');
    const [latestAgreement, setLatestAgreement] = useState<OnboardingAgreement | null>(null);
    const [hasAgreement, setHasAgreement] = useState(false);
    const [agreementFile, setAgreementFile] = useState<File | null>(null);
    const [showError, setShowError] = useState(false);
    const [error, setError] = useState<string | string[]>('');
    const [showConfirm, setShowConfirm] = useState(false);
    const [confirmContent, setConfirmContent] = useState('');
    const [stageChanged, setStageChanged] = useState(false);
    const [productionChanged, setProductionChanged] = useState(false);
    const confirmHandlerRef = useRef<(() => void) | null>(null);
    const tagTermRef = useRef(tagTerm);
    useEffect(() => {
        tagTermRef.current = tagTerm;
    }, [tagTerm]);
    const filterTags = useCallback((tagList: string[], term: string) => (
        (tagList || []).filter(r => !term || r.toLowerCase().indexOf(term.toLowerCase()) > -1)
    ), []);
    const extractErrorMessage = useCallback((e: any) => {
        if (e?.response?.data?.error) {
            return e.response.data.error;
        }
        return 'Unexpected error.';
    }, []);
    const showErrorDialog = useCallback((message: string | string[]) => {
        setError(message);
        setShowError(true);
    }, []);
    const loadConfig = useCallback(async () => {
        const settings = (await axios.get(Config.oauth + '/integration/getconfig')).data;
        const tagsList = settings.Tags || [];
        const productionMessageValue = settings.ProductionMessage || '';
        const stageMessageValue = settings.StageMessage || '';
        const remindersSettings: OnboardingReminderSettings = {
            ResendOnboardingInvite: settings.ResendOnboardingInvite || false,
            ResendInviteAfterDays: settings.ResendInviteAfterDays || '',
            RepeatOnboardingInvite: settings.RepeatOnboardingInvite || false,
            OnboardingInviteSchedule: settings.OnboardingInviteSchedule || '',
            SendOnboardingReminder: settings.SendOnboardingReminder || false,
            SendReminderAfterDays: settings.SendReminderAfterDays || '',
            RepeatOnboardingReminder: settings.RepeatOnboardingReminder || false,
            OnboardingReminderSchedule: settings.OnboardingReminderSchedule || '',
        };
        const stepSettings: OnboardingStepSettings = { EnableShippingLabelStep: settings.EnableShippingLabelStep || false, RequireAllSteps: settings.RequireAllSteps || false };
        const formSettings: OnboardingFormSettings = {AutoAccept: settings.AutoAccept || false, ResetStartOnSubmit: settings.ResetStartOnSubmit || false, CustomFields: settings.CustomFields || []};
        for (let i = 1; i <= 15; i += 1) {
            const id = `CustomField${i}`;
            if (formSettings.CustomFields.filter(r => r.Id === id).length === 0) {
                formSettings.CustomFields.push({ Id: id, Type: 'none' });
            }
        }
        setLoadedTags(tagsList);
        setTags(tagsList);
        setFilteredTags(filterTags(tagsList, tagTermRef.current));
        setProductionMessage(productionMessageValue);
        setStageMessage(stageMessageValue);
        setForm(formSettings);
        setReminders(remindersSettings);
        setSteps(stepSettings);
    }, [filterTags]);
    const loadAgreement = useCallback(async () => {
        try {
            const latest = (await axios.get(Config.oauth + '/integration/getagreement')).data;
            setLatestAgreement(latest);
            setHasAgreement(true);
        } catch (e) {
            setLatestAgreement(null);
            setHasAgreement(false);
        }
    }, []);
    const loadTestCases = useCallback(async () => {
        const cases = (await axios.get(Config.api + '/odata/Company/Functions.GetTestCaseGroups')).data;
        cases.Details = cases.Details.sort((a: any, b: any) => a.Name.localeCompare(b.Name));
        setTestCases(cases);
    }, []);
    useEffect(() => {
        const initialize = async () => {
            if (typeof window !== 'undefined') {
                const active = window.location.hash || '#tags';
                setActiveJump(active);
            }
            if (setTitle) {
                setTitle('Onb. Settings', true);
            }
            await loadConfig();
            await loadAgreement();
            if (!Config.production) {
                await loadTestCases();
            }
        };
        initialize();
    }, [loadAgreement, loadConfig, loadTestCases, setTitle]);
    const saveAgreement = useCallback(async () => {
        if (!agreementFile) {
            return;
        }
        try {
            const formData = new FormData();
            formData.append('file', agreementFile);
            await axios.post(Config.oauth + '/integration/setagreement', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            await loadAgreement();
            setAgreementFile(null);
        } catch (e) {
            showErrorDialog(extractErrorMessage(e));
        }
    }, [agreementFile, extractErrorMessage, loadAgreement, showErrorDialog]);
    const createTag = useCallback(() => {
        const tag = (tagTerm || '').trim();
        if (!tag || tags.indexOf(tag) > -1) {
            return;
        }
        const updatedTags = [...tags, tag];
        setTags(updatedTags);
        setFilteredTags(filterTags(updatedTags, ''));
        setTagTerm('');
    }, [filterTags, tagTerm, tags]);
    const deleteTag = useCallback((tag: string) => {
        const updatedTags = tags.filter(r => r !== tag);
        setTags(updatedTags);
        setFilteredTags(filterTags(updatedTags, tagTermRef.current));
    }, [filterTags, tags]);
    const saveTags = useCallback(async () => {
        try {
            const data = { Tags: tags };
            await axios.post(Config.oauth + '/integration/updatetags', data);
            await loadConfig();
        } catch (e) {
            showErrorDialog(extractErrorMessage(e));
        }
    }, [extractErrorMessage, loadConfig, showErrorDialog, tags]);
    const updateTagTerm = useCallback((e: any) => {
        const value = e.currentTarget.value;
        if (tagTerm === value) {
            return;
        }
        setTagTerm(value);
        setFilteredTags(filterTags(tags, value));
    }, [filterTags, tagTerm, tags]);
    const updateGroupTerm = useCallback((e: any) => {
        const value = e.currentTarget.value;
        if (groupTerm === value) {
            return;
        }
        setGroupTerm(value);
    }, [groupTerm]);
    const saveMessage = useCallback(async (rteValue: string, environment: string) => {
        try {
            let html = rteValue;
            const frag = document.createElement('div');
            frag.innerHTML = html;
            html = frag.innerText ? html : '';
            const data = { Content: html, Environment: environment };
            await axios.post(Config.oauth + '/integration/updatemessage', data);
            await loadConfig();
            if (environment === 'stage') {
                setStageChanged(false);
            } else if (environment === 'production') {
                setProductionChanged(false);
            }
        } catch (e) {
            showErrorDialog(extractErrorMessage(e));
        }
    }, [extractErrorMessage, loadConfig, showErrorDialog]);
    const saveForm = useCallback(async (data: any) => {
        try {
            await axios.post(Config.oauth + '/integration/updateform', data);
            await loadConfig();
        } catch (e) {
            showErrorDialog(extractErrorMessage(e));
        }
    }, [extractErrorMessage, loadConfig, showErrorDialog]);
    const saveReminders = useCallback(async (data: any) => {
        try {
            await axios.post(Config.oauth + '/integration/updatereminders', data);
            await loadConfig();
        } catch (e) {
            showErrorDialog(extractErrorMessage(e));
        }
    }, [extractErrorMessage, loadConfig, showErrorDialog]);
    const saveSteps = useCallback(async (data: any) => {
        try {
            await axios.post(Config.oauth + '/integration/updateonboardingsteps', data);
            await loadConfig();
        } catch (e) {
            showErrorDialog(extractErrorMessage(e));
        }
    }, [extractErrorMessage, loadConfig, showErrorDialog]);
    const saveTestCases = useCallback(async (data: any) => {
        try {
            await axios.post(Config.api + '/odata/Company/Functions.UpdateTestCaseGroups', data);
            await loadTestCases();
        } catch (e: any) {
            const helper = new Helpers();
            const errorMessage = helper.getApiErrors((e.response || {}).data);
            showErrorDialog(errorMessage);
        }
    }, [loadTestCases, showErrorDialog]);
    const newOptionValid = useCallback((value: string, fields: any): string | null | undefined => {
        if (!value) {
            return null;
        }
        if (value.indexOf(',') > -1) {
            return 'Commas are not allowed in options.';
        }
        if (fields && fields.value && fields.value.indexOf(value) > -1) {
            return 'Duplicate options are not allowed.';
        }
        return undefined;
    }, []);
    const openConfirmDialog = useCallback((content: string, handler: () => void) => {
        setConfirmContent(content);
        confirmHandlerRef.current = handler;
        setShowConfirm(true);
    }, []);
    const handleConfirm = useCallback(() => {
        if (confirmHandlerRef.current) {
            confirmHandlerRef.current();
        }
        confirmHandlerRef.current = null;
        setShowConfirm(false);
    }, []);
    const handleCloseConfirm = useCallback(() => {
        confirmHandlerRef.current = null;
        setShowConfirm(false);
    }, []);
    const onTypeChange = useCallback((onChange: (e: any) => void, e: any, oldValue: any) => {
        const val = e.target.value;
        if (val === 'none' && oldValue !== 'none' && oldValue) {
            openConfirmDialog(
                'Changing this field type to None/Not Used will delete this question and remove any existing supplier data. Are you sure you want to continue?',
                () => onChange(e)
            );
            return;
        } else if ((oldValue === 'select' || oldValue === 'multiselect') && !(val === 'select' || val === 'multiselect')) {
            openConfirmDialog(
                'Changing to this field type will remove all existing options. Are you sure you want to continue?',
                () => onChange(e)
            );
            return;
        }
        onChange(e);
    }, [openConfirmDialog]);
    const removeGroup = useCallback((args: any[], state: any, utils: any) => {
        if (args[2] === true) {
            utils.changeValue(state, 'Groups', (v: string[]) => v.filter(r => r !== args[0]));
            for (let i = 0; i < state.formState.values.Details.length; i += 1) {
                const details = state.formState.values.Details[i];
                if (details.Groups && details.Groups.filter((r: string) => r === args[0]).length > 0) {
                    utils.changeValue(state, `Details[${i}].Groups`, (v: string[]) => v.filter(r => r !== args[0]));
                }
            }
        } else {
            openConfirmDialog(
                'Deleting this group will automatically unassign test cases associated with it. Suppliers that were a part of this test group will now see all test cases. Are you sure you want to continue?',
                () => args[1](args[0], args[1], true)
            );
        }
    }, [openConfirmDialog]);
    const handleJumpSelection = useCallback((target: string) => {
        setActiveJump(prev => (prev === target ? prev : target));
    }, []);
    const jumpSections: JumpSection[] = [
        { href: '#tags', label: 'Tags' },
        { href: '#vendorAgreement', label: 'Supplier Agreement' },
        { href: '#stageMessage', label: 'Stage Message', hidden: Config.production },
        { href: '#productionMessage', label: 'Production Message' },
        { href: '#onboardingForm', label: 'Onboarding Form' },
        { href: '#onboardingSteps', label: 'Onboarding Steps' },
        { href: '#reminders', label: 'Supplier Reminders' },
        { href: '#testcases', label: 'Test Cases', hidden: Config.production }
    ];
    return (
        <RootGrid container style={{gap: pageContentGap, padding: "24px 14px 24px 14px", position: "relative"}}>
            <Grid container justifyContent={"space-between"} direction={"row"}>
                <Grid item container justifyContent={"flex-start"} alignItems={"center"} style={{gap: pageContentGap, width: "100%"}}>
                    <IconButton
                        sx={iconButtonSx}
                        style={{height: "36px", width: "36px", position: 'relative', zIndex: 1}}
                        aria-label='Go back to onboarding'
                        onClick={(event: React.MouseEvent) => {
                            event.preventDefault();
                            if (!location || location.key === undefined) {
                                history.push('/onboarding');
                            } else {
                                history.goBack();
                            }
                        }}
                        disabled={!history}
                        size="large">
                        <ArrowBack style={{height: "18px", width: "18px"}}/>
                    </IconButton>
                    <HeaderTypography>
                        Settings
                    </HeaderTypography>
                </Grid>
            </Grid>
            <Grid size={12}>
                <div style={{display: 'flex', flexWrap: 'wrap', gap: '1em'}}>
                    {jumpSections.filter(section => !section.hidden).map(section => (
                        <JumpToTab
                            key={section.href}
                            component='a'
                            href={section.href}
                            selected={activeJump === section.href ? 1 : 0}
                            onClick={() => handleJumpSelection(section.href)}
                        >
                            {section.label}
                        </JumpToTab>
                    ))}
                </div>
            </Grid>
            <Grid id="tags" size={12} style={{ display: 'flex' }}>
                <Card style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <SectionToolbar>Tags</SectionToolbar>
                    <CardContent style={{ textAlign: 'left' }}>
                        <Typography>Add and manage supplier tags. Tags can be seen from the Onboarding page to provide classification for suppliers based on things like category, merchandiser, etc.</Typography>
                        <div style={{ display: 'flex', gap: '1em', marginTop: '1em' }}>
                            <SearchBox inputStyle={{ width: '15em' }} term={tagTerm} handleKeyUp={updateTagTerm} onEnter={updateTagTerm} handleChange={updateTagTerm} placeholder='Search or create tags' />
                            <Button variant='contained' color='primary' onClick={createTag} disabled={!tagTerm}>Create</Button>
                        </div>
                        <div style={{ marginTop: '1em', marginBottom: '1em', display: 'flex', gap: '0.5em', flexWrap: 'wrap' }}>
                            {filteredTags.map(r => <Chip variant='outlined' key={r} label={r} onDelete={() => deleteTag(r)} />)}
                        </div>
                        <DownloadButton variant='contained' color='primary' onClick={saveTags} disabled={isEqual(tags, loadedTags)}>Save</DownloadButton>
                    </CardContent>
                </Card>
            </Grid>
            <Grid id="vendorAgreement" size={12} style={{ display: 'flex' }}>
                <Card style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <SectionToolbar>Supplier Agreement</SectionToolbar>
                    <CardContent style={{ textAlign: 'left' }}>
                        <Typography>Upload a PDF file of the agreement you would like your suppliers to review and sign from the Onboarding form. Only 1 file is supported.</Typography>
                        <DropZone accept=".pdf" style={{ width: '100%', height: '10em', marginTop: '1em', marginBottom: '1em' }} onDrop={(files: File[]) => { setAgreementFile(files[0]); }}>
                            {({ isDragAccept, isDragReject }: any) => {
                                if (isDragAccept || isDragReject) {
                                    return "Drop here to upload this file.";
                                }
                                return agreementFile ? agreementFile.name : 'Drop a PDF file here or click to choose a file.';
                            }}
                        </DropZone>
                        <Button component="a" target="_blank" href={Config.oauth + "/integration/downloadagreement/" + ((latestAgreement || {}).Id || "")} variant='outlined' disabled={!hasAgreement} style={{ minWidth: '10em', marginRight: '1em' }}>View Current Agreement</Button>
                        <DownloadButton variant='contained' color='primary' onClick={saveAgreement} disabled={!agreementFile}>Save</DownloadButton>
                    </CardContent>
                </Card>
            </Grid>
            {!Config.production && <Grid id="stageMessage" size={12} style={{ display: 'flex' }}>
                <Card style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <SectionToolbar>Stage Message</SectionToolbar>
                    <CardContent style={{ textAlign: 'left' }}>
                        <Typography>Share a message with your suppliers from the stage dashboard. Examples of what you may want to include are: non-Logicbroker onboarding tasks, alerts and additional directions.</Typography>
                        <div style={{ height: '200px', display: 'flex', marginTop: '1em', marginBottom: '1em' }}>
                            <SimpleRte className='textEditor' value={stageMessage} onChange={(html: string, isEmpty: boolean) => { setStageMessage(html); setStageChanged(true); }} />
                        </div>
                        <DownloadButton disabled={!stageChanged} variant='contained' color='primary' onClick={() => saveMessage(stageMessage, 'stage')}>Save</DownloadButton>
                    </CardContent>
                </Card>
            </Grid>}
            <Grid id="productionMessage" size={12} style={{ display: 'flex' }}>
                <Card style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <SectionToolbar>Production Message</SectionToolbar>
                    <CardContent style={{ textAlign: 'left' }}>
                        <Typography>Share a message with your suppliers from the production dashboard. Examples of what you may want to include are: non-Logicbroker onboarding tasks, alerts and additional directions.</Typography>
                        <div style={{ height: '200px', display: 'flex', marginTop: '1em', marginBottom: '1em' }}>
                            <SimpleRte className='textEditor' value={productionMessage} onChange={(html: string, isEmpty: boolean) => { setProductionMessage(html); setProductionChanged(true); }} />
                        </div>
                        <DownloadButton disabled={!productionChanged} variant='contained' color='primary' onClick={() => saveMessage(productionMessage, 'production')}>Save</DownloadButton>
                    </CardContent>
                </Card>
            </Grid>
            <Grid id="onboardingForm" size={12} style={{ display: 'flex' }}>
                <Card style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <SectionToolbar>Onboarding Form</SectionToolbar>
                    <CardContent style={{ textAlign: 'left' }}>
                        <Form onSubmit={saveForm}
                            mutators={{ ...arrayMutators }}
                            initialValues={form}
                            render={({ handleSubmit, pristine, invalid, form }) => (
                                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column' }}>
                                    <Typography variant='h6'>Settings</Typography>
                                    <FormControl>
                                        <FormControlLabel label='Auto-accept onboarding form' control={<Field type="checkbox" component={Switch as any} name="AutoAccept" />} />
                                        <FormControlLabel label='Reset start date when supplier completes the onboarding form' control={<Field type="checkbox" component={Switch as any} name="ResetStartOnSubmit" />} />
                                    </FormControl>
                                    <Typography variant='h6' style={{ marginTop: '1rem' }}>Custom Questions</Typography>
                                    <Typography>Add custom questions to your supplier's onboarding form.</Typography>
                                    <FieldArray name='CustomFields'>
                                        {({ fields }) =>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '1rem' }}>
                                            {fields.map((name, index) => <div key={name} style={{ maxWidth: '50rem', borderRadius: '4px', border: '1px solid #aaa', backgroundColor: "#f4f4f4", padding: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                                <Typography style={{ fontWeight: 'bold', marginTop: '0.5rem', marginBottom: '0.5rem' }}>Question #{fields.value[index!].Id.replace("CustomField", "")}</Typography>
                                                <Field style={{ width: '100%' }} component={ConfirmSelectField as any} onChange={onTypeChange} label='Question type' name={`${name}.Type`}>
                                                    {fieldTypes.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
                                                </Field>
                                                {fields.value[index!].Type !== "none" && <>
                                                    <div><Field component={TextField} style={{ width: '100%' }} label='Field name' name={`${name}.Name`} validate={v => v ? undefined : 'Field name is required.'} /></div>
                                                    <div><Field component={TextField} style={{ width: '100%' }} label='Description' name={`${name}.Description`} /></div>
                                                    <div style={{ width: '100%', textAlign: 'right' }}><Field component={SwitchField} label='Required' name={`${name}.Required`} type="checkbox" /></div>
                                                    {(fields.value[index!].Type === "select" || fields.value[index!].Type === "multiselect") && <>
                                                        <Typography style={{ fontWeight: 'bold', marginTop: '0.5rem', marginBottom: '0.5rem' }}>Options</Typography>
                                                        <FieldArray name={`${name}.Options`}>
                                                            {({ fields: optionFields }) => <div>
                                                                {optionFields.map((optionName, optionIndex) => <div key={optionName} style={{ borderBottom: '1px solid #aaa', padding: '0.25rem', display: 'flex' }}>
                                                                    <span style={{ flex: 1 }}>{optionFields.value[optionIndex!]}</span>
                                                                    <IconButton
                                                                        aria-label='Remove option'
                                                                        onClick={() => optionFields.remove(optionIndex!)}
                                                                        style={{ padding: '2px' }}
                                                                        size="large"><CloseIcon style={{ height: '0.75em', width: '0.75em' }} /></IconButton>
                                                                </div>)}
                                                                <div style={{ marginTop: '0.5rem' }}>
                                                                    <Field component={TextField} name={`${name}.option`} style={{ marginRight: '2em', verticalAlign: 'bottom', minWidth: '20em' }} placeholder="New option" validate={v => newOptionValid(v, optionFields)} alwaysShowError={true} />
                                                                    <Button variant='outlined' onClick={() => { optionFields.push(fields.value[index!].option); form.change(`${name}.option`, ''); }} disabled={!fields.value[index!].option || !!newOptionValid(fields.value[index!].option, optionFields)}>Add option</Button>
                                                                </div>
                                                            </div>}
                                                        </FieldArray>
                                                    </>}
                                                </>}
                                            </div>)}
                                            </div>
                                        }
                                    </FieldArray>
                                    <div style={{ marginTop: '0.5rem' }}><DownloadButton disabled={invalid || pristine} variant='contained' color='primary' onClick={handleSubmit}>Save</DownloadButton></div>
                                </form>)}
                        />
                    </CardContent>
                </Card>
            </Grid>
            <Grid id="onboardingSteps" size={12} style={{ display: 'flex' }}>
                <Card style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <SectionToolbar>Onboarding Steps</SectionToolbar>
                    <CardContent style={{ textAlign: 'left' }}>
                        <Form onSubmit={saveSteps}
                            initialValues={steps}
                            render={({ handleSubmit, pristine }) => (
                                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column' }}>
                                    <Typography>Choose which optional steps your suppliers must complete during onboarding. These settings apply to all of your suppliers.</Typography>
                                    <Typography variant='h6' style={{ marginTop: '1rem' }}>Shipping Label Approval</Typography>
                                    <div>
                                        <Field component={SwitchField} name='EnableShippingLabelStep' type='checkbox' label='Require suppliers to upload a sample shipping label for review' />
                                    </div>
                                    <Typography variant='h6' style={{ marginTop: '1rem' }}>Go Live Requests</Typography>
                                    <div>
                                        <Field component={SwitchField} name='RequireAllSteps' type='checkbox' label='Require onboarding steps to be completed before suppliers can request to go live' />
                                    </div>
                                    <div style={{ marginTop: '0.5rem' }}><DownloadButton disabled={pristine} variant='contained' color='primary' onClick={handleSubmit}>Save</DownloadButton></div>
                                </form>)}
                        />
                    </CardContent>
                </Card>
            </Grid>
            <Grid id="reminders" size={12} style={{ display: 'flex' }}>
                <Card style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <SectionToolbar>Supplier Reminders</SectionToolbar>
                    <CardContent style={{ textAlign: 'left' }}>
                        <Form onSubmit={saveReminders}
                            mutators={{ ...arrayMutators }}
                            initialValues={reminders}
                            render={({ handleSubmit, pristine, invalid, values }) => (
                                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column' }}>
                                    <Typography>Configure automated reminder notifications to be sent to suppliers by email when their status has not changed. After 4 attempts, we will stop further outreach and you will see an alert on the supplier's page. At this point, please reach out to the supplier directly to discuss the partnership.</Typography>
                                    <Typography variant='h6' style={{ marginTop: '1rem' }}>Resend Invitation</Typography>
                                    <div>
                                        <div>
                                            <Field component={SwitchField} name='ResendOnboardingInvite' type='checkbox' label={<div style={{ display: 'flex', alignItems: 'baseline' }}>Resend onboarding invitation after <Field component={ExtendedTextField} validate={(v, allValues: any) => (allValues.ResendOnboardingInvite && !v) || v < 1 || v > 20 ? true : undefined} inputProps={{ readOnly: !values.ResendOnboardingInvite, max: 20, min: 1 }} style={{ margin: '0 0.5em 0 0.5em', width: '4em' }} name='ResendInviteAfterDays' type='number' /> days of account in an 'Invite Sent' status.</div>} />
                                        </div>
                                        <div>
                                            <Field component={SwitchField} name='RepeatOnboardingInvite' type='checkbox' label={<div style={{ display: 'flex', alignItems: 'baseline' }}>Repeat <Field component={SelectField as any} validate={(v, allValues: any) => allValues.RepeatOnboardingInvite && !v ? true : undefined} inputProps={{ readOnly: !values.RepeatOnboardingInvite }} style={{ margin: '0 0.5em 0 0.5em', width: '10em' }} name='OnboardingInviteSchedule'>{scheduleOptions.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}</Field></div>} />
                                        </div>
                                    </div>
                                    <Typography variant='h6' style={{ marginTop: '1rem' }}>Onboarding Reminder</Typography>
                                    <div>
                                        <div>
                                            <Field component={SwitchField} name='SendOnboardingReminder' type='checkbox' label={<div style={{ display: 'flex', alignItems: 'baseline' }}>Send reminder to complete onboarding steps after <Field component={ExtendedTextField} validate={(v, allValues: any) => allValues.SendOnboardingReminder && !v ? true : undefined} inputProps={{ readOnly: !values.SendOnboardingReminder, max: 20, min: 1 }} style={{ margin: '0 0.5em 0 0.5em', width: '4em' }} name='SendReminderAfterDays' type='number' /> days of account in an 'Onboarding' status.</div>} />
                                        </div>
                                        <div>
                                            <Field component={SwitchField} name='RepeatOnboardingReminder' type='checkbox' label={<div style={{ display: 'flex', alignItems: 'baseline' }}>Repeat <Field component={SelectField as any} validate={(v, allValues: any) => (allValues.RepeatOnboardingReminder && !v) || v < 1 || v > 20 ? true : undefined} inputProps={{ readOnly: !values.RepeatOnboardingReminder }} style={{ margin: '0 0.5em 0 0.5em', width: '10em' }} name='OnboardingReminderSchedule'>{scheduleOptions.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}</Field></div>} />
                                        </div>
                                    </div>
                                    <div style={{ marginTop: '0.5rem' }}><DownloadButton disabled={invalid || pristine} variant='contained' color='primary' onClick={handleSubmit}>Save</DownloadButton></div>
                                </form>)}
                        />
                    </CardContent>
                </Card>
            </Grid>
            {!Config.production && <Grid id="testcases" size={12} style={{ display: 'flex' }}>
                <Card style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <SectionToolbar>Test Cases</SectionToolbar>
                    <CardContent style={{ textAlign: 'left' }}>
                        <Typography variant='h6'>Supplier-Specific Test Cases</Typography>
                        <Form onSubmit={saveTestCases}
                            mutators={{ ...arrayMutators, removeGroup }}
                            initialValues={testCases}
                            render={({ handleSubmit, pristine, invalid, form, values }) => (
                                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column' }}>
                                    <Typography>By default, all test cases will be visible to all suppliers. To enable test cases by supplier, toggle on the setting below and configure the test cases.</Typography>
                                    <div>
                                        <Field component={SwitchField} name='Enabled' type='checkbox' label={<div style={{ display: 'flex', alignItems: 'baseline' }}>Enable test cases by supplier</div>} />
                                    </div>
                                    {values.Enabled && <div>
                                        <Typography variant='h6' style={{ marginTop: '1rem' }}>Test Groups</Typography>
                                        <Typography>Name each supplier test group - you will then use these to apply to each test case (ex. EDI Suppliers, Portal Suppliers, Parcel Suppliers, etc.).</Typography>
                                        <FieldArray name="Groups">
                                            {({ fields }) => <>
                                                <div style={{ display: 'flex', gap: '1em', marginTop: '1em' }}>
                                                    <SearchBox inputStyle={{ width: '15em' }} term={groupTerm} handleKeyUp={updateGroupTerm} onEnter={updateGroupTerm} handleChange={updateGroupTerm} placeholder='Add test group' />
                                                    <Button variant='outlined' onClick={() => { fields.push(groupTerm); setGroupTerm(""); }} disabled={!groupTerm || values.Groups.filter((r: string) => r === groupTerm).length > 0}>Add</Button>
                                                </div>
                                                <div style={{ marginTop: '1em', marginBottom: '1em', display: 'flex', gap: '1em' }}>
                                                    {fields.map((name, index) => <Chip variant='outlined' key={name} label={fields.value[index!]} onDelete={() => form.mutators.removeGroup(fields.value[index!], form.mutators.removeGroup)} />)}
                                                </div>
                                            </>}
                                        </FieldArray>
                                        <Typography variant='h6' style={{ marginTop: '1rem' }}>Test Cases</Typography>
                                        <Typography>Choose which test group(s) can see each test case. Please note that suppliers that are not assigned to a group will see all test cases by default.</Typography>
                                        <FieldArray name="Details">
                                            {({ fields }) =>
                                                fields.map((name, index) => <div key={name} style={{ maxWidth: '50rem', borderRadius: '4px', border: '1px solid #aaa', backgroundColor: "#f4f4f4", marginTop: '1rem', marginBottom: '0.5rem', padding: '0.5rem' }}>
                                                    <Typography style={{ fontWeight: 'bold', marginTop: '0.5rem', marginBottom: '0.5rem' }}>{fields.value[index!].Name}</Typography>
                                                    <Typography>Supplier test groups that will see this test case:</Typography>
                                                    <Field name={`${name}.Groups`} component={FastSelect as any} style={{ width: '100%' }} placeholder='All suppliers not part of a test group' options={values.Groups.map((r: string) => ({ label: r, value: r }))} />
                                                </div>)
                                            }
                                        </FieldArray>
                                    </div>}
                                    <div style={{ marginTop: '0.5rem' }}><DownloadButton disabled={invalid || pristine} variant='contained' color='primary' onClick={handleSubmit}>Save</DownloadButton></div>
                                </form>)}
                        />
                    </CardContent>
                </Card>
            </Grid>}
            <Dialog open={showError} onClose={() => { setShowError(false); }}>
                <SectionToolbar>Error</SectionToolbar>
                <DialogContent><Typography>{error}</Typography></DialogContent>
            </Dialog>
            <ConfirmDialog open={showConfirm} onClose={handleCloseConfirm} message={confirmContent} onConfirm={handleConfirm} />
        </RootGrid>
    );
};
export default OnboardingSettingsV2;
